import { PROTOCOL_TEST_IDS } from '@/components/admin/protocoles/contracts';
import { ObjectiveRow } from '@/components/admin/protocoles/step/objective-row';
import { duplicateObjective, objectiveDuplicationBlocked } from '@/lib/duplication';
import {
  moveObjective,
  newObjective,
  removeObjective,
  replaceObjective,
} from '@/lib/protocol-step-draft';
import type {
  CriterionCard,
  ProtocolObjectiveDraft,
  ProtocolStepDraft,
} from '@matter_extractor/shared';
import { PROTOCOL_OBJECTIVES_MAX } from '@matter_extractor/shared';
import { Icon } from '@pms-digital/design-system';
import type { DragEvent as ReactDragEvent } from 'react';
import { useState } from 'react';

/**
 * The objectives of a step: expandable rows, ONE open at a time, reordered by
 * drag and drop (refonte modulaire §7.2).
 *
 * Which row is open lives here and not in the rows, because « one at a time »
 * is a property of the LIST: held per row, two rows could both be open and
 * nothing would be wrong from either one's point of view. The same goes for the
 * drag: a row knows it is being dragged, only the list knows where it would
 * land.
 *
 * Adding an objective OPENS it. A row that appeared folded, named « Objectif 3 »
 * and carrying a yellow « aucun critère », would ask the operator to find and
 * click the thing they just asked for.
 *
 * The order of the objectives is not the order they are evaluated in - §8.3
 * reads them all every turn - but it is the order an operator reads them in,
 * and the direction of the step is written against that reading.
 */

export interface ObjectiveListProps {
  step: ProtocolStepDraft;
  /** The whole draft: an objective's direction may cite a previous step's. */
  steps: readonly ProtocolStepDraft[];
  criteria: readonly CriterionCard[];
  criteriaRead: boolean;
  readOnly: boolean;
  onChangeObjectives: (next: readonly ProtocolObjectiveDraft[]) => void;
}

/** What a drag is carrying and where it would land. */
type Drag = { readonly id: string; readonly overId: string | null; readonly after: boolean };

export function ObjectiveList({
  step,
  steps,
  criteria,
  criteriaRead,
  readOnly,
  onChangeObjectives,
}: ObjectiveListProps) {
  const [openId, setOpenId] = useState<string | null>(null);
  /**
   * The copy a duplication just made, whose name field takes the focus (§4.2).
   *
   * Beside `openId` rather than folded into it: an objective OPENED by a click
   * must not steal the caret, and only a duplication asks for that. Cleared as
   * soon as the row says it has the focus, because a row keyed by its id
   * REMOUNTS when the operator comes back to this step - and a request still
   * standing would grab the caret a second time, on a screen they were only
   * reading.
   */
  const [focusId, setFocusId] = useState<string | null>(null);
  const [drag, setDrag] = useState<Drag | null>(null);

  const objectives = step.objectives;
  const duplicationBlocked = objectiveDuplicationBlocked(objectives);

  function drop(targetId: string): void {
    if (drag === null) return;
    const from = objectives.findIndex((one) => one.id === drag.id);
    let to = objectives.findIndex((one) => one.id === targetId);
    setDrag(null);
    if (from < 0 || to < 0 || from === to) return;
    // `to` is an index in the list WITHOUT the dragged row, which is what
    // `moveObjective` takes: dropping after a row that sits below the dragged
    // one is the case the two spellings disagree on.
    if (from < to) to -= 1;
    onChangeObjectives(moveObjective(objectives, from, drag.after ? to + 1 : to));
  }

  return (
    <div>
      {objectives.length === 0 ? (
        <p className="text-caption text-gray-600 leading-4">
          Aucun objectif : l’étape se joue sur sa seule direction et se clôture au premier tour.
        </p>
      ) : (
        <div className="overflow-hidden rounded-md border-2 border-gray-200">
          {objectives.map((objective, index) => (
            <ObjectiveRow
              key={objective.id}
              objective={objective}
              index={index}
              open={openId === objective.id}
              steps={steps}
              stepId={step.id}
              criteria={criteria}
              criteriaRead={criteriaRead}
              readOnly={readOnly}
              directionForScribe={step.hideObjectives}
              focusName={focusId === objective.id}
              onNameFocused={() => setFocusId(null)}
              duplicationBlocked={duplicationBlocked}
              onToggle={() => setOpenId((was) => (was === objective.id ? null : objective.id))}
              onChange={(next) => onChangeObjectives(replaceObjective(objectives, next))}
              onDelete={() => {
                setOpenId(null);
                onChangeObjectives(removeObjective(objectives, objective.id));
              }}
              // The copy OPENS, which shuts its source - one row open at a
              // time, and it is the LIST that holds which (§4.2, §7.2).
              onDuplicate={() => {
                const done = duplicateObjective(objectives, objective.id);
                if (done === null) return;
                onChangeObjectives(done.list);
                setOpenId(done.cloneId);
                setFocusId(done.cloneId);
              }}
              dropHint={
                drag === null || drag.overId !== objective.id
                  ? null
                  : drag.after
                    ? 'after'
                    : 'before'
              }
              dragging={drag?.id === objective.id}
              onDragStart={() => setDrag({ id: objective.id, overId: null, after: false })}
              onDragOver={(event: ReactDragEvent<HTMLElement>) => {
                if (drag === null) return;
                event.preventDefault();
                const box = event.currentTarget.getBoundingClientRect();
                const after = event.clientY > box.top + box.height / 2;
                setDrag((was) => (was === null ? null : { ...was, overId: objective.id, after }));
              }}
              onDrop={(event: ReactDragEvent<HTMLElement>) => {
                event.preventDefault();
                drop(objective.id);
              }}
              onDragEnd={() => setDrag(null)}
            />
          ))}
        </div>
      )}

      {readOnly ? null : (
        <button
          type="button"
          data-testid={PROTOCOL_TEST_IDS.addObjective}
          disabled={objectives.length >= PROTOCOL_OBJECTIVES_MAX}
          onClick={() => {
            const fresh = newObjective(objectives.length);
            setOpenId(fresh.id);
            onChangeObjectives([...objectives, fresh]);
          }}
          className="mt-2.5 inline-flex h-9 cursor-pointer items-center gap-2 rounded-md px-3.5 font-semibold text-bleu-nuit text-small hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit disabled:cursor-not-allowed disabled:text-gray-500 disabled:hover:bg-transparent"
        >
          <Icon name="add" size={16} />
          Ajouter un objectif
        </button>
      )}
    </div>
  );
}
